import { useEffect, useRef, useState } from "react";
import { Common, Icon } from "@/components";
import { ModalContent } from "@/components/Modal";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import {
  LocationWithComponents,
  MapProps,
  extractAddressComponents,
  AddressComponents,
  combineAddressComponents,
  isMissingAddressComponents,
} from "@/utils/address";
import {
  GOOGLE_API_KEY,
  GOOGLE_MAP_OPTIONS,
} from "@/configuration/globalVariable";
import ReactTooltip from "react-tooltip";
import { SnackbarUtils } from "@/utils/notistack";

type EditMode = "manual" | "map";

// Loading state renderer
const renderStatus = (status: Status) => {
  if (status === Status.LOADING)
    return (
      <div className="w-full h-full flex items-center justify-center">
        Loading maps...
      </div>
    );

  if (status === Status.FAILURE) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        Error loading maps
      </div>
    );
  }

  return <></>;
};

const MissingAddressComponentsWarning = () => {
  const tooltip =
    "This address may not be specific enough for delivery. Please review and add missing details if possible.";

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  return (
    <span className="text-neutral-50 cursor-help" data-tip={tooltip}>
      <Icon.WarningCircle className="fill-yellow" size="16" />
    </span>
  );
};
// The actual map component that gets rendered when Google Maps API is loaded
const MapComponent = ({
  currentAddress,
  initialLocation,
  onLocationSelect,
}: MapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  const [searchBox, setSearchBox] =
    useState<google.maps.places.SearchBox | null>(null);
  const [selectedLocation, setSelectedLocation] =
    useState<LocationWithComponents | null>(initialLocation || null);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !map) {
      // Default to a central location if no initial location
      const defaultCenter = { lat: -25.344, lng: 131.031 };
      const center = initialLocation
        ? {
            lat: initialLocation.lat ?? defaultCenter.lat,
            lng: initialLocation.lng ?? defaultCenter.lng,
          }
        : defaultCenter;

      // Create the map
      const mapInstance = new google.maps.Map(mapRef.current, {
        zoom: initialLocation?.lat ? 11 : 4,
        center,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        fullscreenControl: false,
      });

      // Create marker
      const markerInstance = new google.maps.Marker({
        position: center,
        map: mapInstance,
        draggable: true,
        animation: google.maps.Animation.DROP,
      });

      setMap(mapInstance);
      setMarker(markerInstance);

      // Update selected location when marker is dragged
      markerInstance.addListener("dragend", () => {
        const position = markerInstance.getPosition();
        if (position) {
          const lat = position.lat();
          const lng = position.lng();

          // Get address from coordinates (reverse geocoding)
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              // Extract address components
              const addressComponents = extractAddressComponents(
                results[0].address_components
              );

              const newLocation = {
                lat,
                lng,
                address: combineAddressComponents(addressComponents),
                components: addressComponents,
              };
              setSelectedLocation(newLocation);
              onLocationSelect(newLocation);
            }
          });
        }
      });
    }
  }, [mapRef, initialLocation, onLocationSelect, map]);

  // Setup search box
  useEffect(() => {
    if (map && !searchBox) {
      const input = document.getElementById(
        "map-search-input"
      ) as HTMLInputElement;
      if (input) {
        const searchBoxInstance = new google.maps.places.SearchBox(input);

        // Bias searchbox results towards current map bounds
        map.addListener("bounds_changed", () => {
          searchBoxInstance.setBounds(
            map.getBounds() as google.maps.LatLngBounds
          );
        });

        // Listen for search results
        searchBoxInstance.addListener("places_changed", () => {
          const places = searchBoxInstance.getPlaces();

          if (!places || places.length === 0) {
            return;
          }

          const place = places[0];

          if (place.geometry && place.geometry.location) {
            // Set map center to the selected place
            map.setCenter(place.geometry.location);
            map.setZoom(17);

            // Update marker position
            if (marker) {
              marker.setPosition(place.geometry.location);
            }

            // Update selected location
            // Extract address components
            const addressComponents = extractAddressComponents(
              place.address_components || []
            );

            const newLocation = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              address: combineAddressComponents(addressComponents),
              components: addressComponents,
            };
            setSelectedLocation(newLocation);
            onLocationSelect(newLocation);
          }
        });

        setSearchBox(searchBoxInstance);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, marker, searchBox, onLocationSelect]);

  return (
    <>
      <div className="mb-4">
        <Common.Input
          id="map-search-input"
          type="text"
          iconRight={Icon.Search}
          iconClassName="stroke-neutral-40"
          placeholder="Search for a location"
          className="w-full"
          defaultValue={currentAddress}
        />
      </div>
      <div
        ref={mapRef}
        className="w-full h-[400px] border border-neutral-20 rounded-md mb-4"
      />
      <div className="mt-2">
        {selectedLocation?.address && (
          <div className="flex items-center space-x-1">
            <span className="font-medium text-neutral-60">
              Selected address:
            </span>
            <span className="font-semibold text-neutral-90">
              {selectedLocation.address}
            </span>
            {selectedLocation.components &&
              isMissingAddressComponents(selectedLocation.components) && (
                <MissingAddressComponentsWarning />
              )}
          </div>
        )}
      </div>
    </>
  );
};

interface LocateAddressDialogProps {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  title: string;
  description?: string;
  currentAddress?: string;
  initialLocation?: LocationWithComponents;
  onConfirm: (location: LocationWithComponents) => void;
}

const LocateAddressDialog = ({
  open,
  onClose,
  loading = false,
  title,
  description,
  currentAddress = "",
  initialLocation,
  onConfirm,
}: LocateAddressDialogProps) => {
  const [selectedLocation, setSelectedLocation] =
    useState<LocationWithComponents>(
      initialLocation || {
        lat: undefined,
        lng: undefined,
        address: "",
        components: {
          addressLine1: "",
          addressLine2: "",
          suburb: "",
          state: "",
          postcode: "",
          country: "",
        },
      }
    );
  const [editMode, setEditMode] = useState<EditMode>("map");

  const [isGeocoding, setIsGeocoding] = useState(false);

  // Reset selected location when dialog opens with new initialLocation
  useEffect(() => {
    if (open) {
      setSelectedLocation(
        initialLocation || {
          lat: undefined,
          lng: undefined,
          address: "",
          components: {
            addressLine1: "",
            addressLine2: "",
            suburb: "",
            state: "",
            postcode: "",
            country: "",
          },
        }
      );
      setEditMode("map");
    }
  }, [open, initialLocation]);

  const handleLocationSelect = (location: LocationWithComponents) => {
    setSelectedLocation(location);
  };

  // Enable manual input mode
  const toggleManualMode = () => {
    if (!selectedLocation.lat || !selectedLocation.lng) {
      // get geocode from address
      setIsGeocoding(true);
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { address: selectedLocation.address },
        (results, status) => {
          if (status === "OK" && results && results[0]) {
            setSelectedLocation({
              ...selectedLocation,
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });
            setEditMode((prev) => (prev === "manual" ? "map" : "manual"));
            setIsGeocoding(false);
          } else {
            SnackbarUtils.error("Failed to geocode address");
            setIsGeocoding(false);
          }
        }
      );
    } else {
      setEditMode((prev) => (prev === "manual" ? "map" : "manual"));
    }
  };

  // Handle changes to manual address fields
  const handleManualAddressChange = (
    field: keyof AddressComponents,
    value: string
  ) => {
    const updatedComponents = {
      ...selectedLocation.components,
      [field]: value,
    };

    const updatedLocation: LocationWithComponents = {
      ...selectedLocation,
      lat: undefined,
      lng: undefined,
      address: combineAddressComponents(updatedComponents),
      components: {
        ...updatedComponents,
      },
    };
    setSelectedLocation(updatedLocation);
  };

  // Handle confirm button click
  const handleConfirm = () => {
    if (!selectedLocation.lat || !selectedLocation.lng) {
      setIsGeocoding(true);
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { address: selectedLocation.address },
        (results, status) => {
          if (status === "OK" && results && results[0]) {
            const geocodedLocation = {
              ...selectedLocation,
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            };

            setSelectedLocation(geocodedLocation);

            if (
              onConfirm &&
              geocodedLocation &&
              geocodedLocation.lat &&
              geocodedLocation.lng
            ) {
              onConfirm(geocodedLocation);
            }
          } else {
            SnackbarUtils.error("Failed to geocode address");
          }
          setIsGeocoding(false);
        }
      );
    } else {
      if (
        onConfirm &&
        selectedLocation &&
        selectedLocation.lat &&
        selectedLocation.lng
      ) {
        onConfirm(selectedLocation);
      }
    }
  };

  return (
    <ModalContent
      open={open}
      onClose={onClose}
      panelClassWidth="sm:max-w-[800px] sm:w-full"
    >
      <div className="p-6">
        <h3 className="text-hd5 font-medium mb-4">{title}</h3>
        {description && <p className="text-neutral-60 mb-4">{description}</p>}

        {editMode === "map" && GOOGLE_API_KEY ? (
          <>
            <Wrapper render={renderStatus} {...GOOGLE_MAP_OPTIONS}>
              <MapComponent
                currentAddress={currentAddress || ""}
                initialLocation={selectedLocation}
                onLocationSelect={handleLocationSelect}
              />
            </Wrapper>
            <div className="p-0 pl-0 flex items-center">
              <p className="text-neutral-60">
                Cannot locate the address?{" "}
                <Common.Button
                  variant="link"
                  onClick={toggleManualMode}
                  className="font-medium flex items-center px-0"
                >
                  Click here to fill the address manually
                </Common.Button>
              </p>
            </div>
          </>
        ) : null}

        {/* Manual address form */}
        {editMode === "manual" && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-6">
              <Common.Button
                color="transparent"
                variant="link"
                iconLeft={Icon.Back}
                onClick={toggleManualMode}
                className="font-medium flex items-center px-0"
              >
                Back to Map
              </Common.Button>
            </div>

            <div>
              <div className="mb-5">
                <Common.Input
                  label="Address Line 1"
                  isAsterisk
                  type="text"
                  value={selectedLocation?.components?.addressLine1}
                  onChange={(e) =>
                    handleManualAddressChange("addressLine1", e.target.value)
                  }
                  placeholder="Street number and name"
                  className="w-full"
                />
              </div>

              <div className="mb-5">
                <Common.Input
                  label="Address Line 2"
                  type="text"
                  value={selectedLocation?.components?.addressLine2}
                  onChange={(e) =>
                    handleManualAddressChange("addressLine2", e.target.value)
                  }
                  placeholder="Apartment, suite, unit, etc. (optional)"
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5 mb-5">
                <div>
                  <Common.Input
                    label="Suburb"
                    isAsterisk
                    type="text"
                    value={selectedLocation?.components?.suburb}
                    onChange={(e) =>
                      handleManualAddressChange("suburb", e.target.value)
                    }
                    placeholder="Suburb"
                    className="w-full"
                  />
                </div>

                <div>
                  <Common.Input
                    label="State"
                    isAsterisk
                    type="text"
                    value={selectedLocation?.components?.state}
                    onChange={(e) =>
                      handleManualAddressChange("state", e.target.value)
                    }
                    placeholder="State"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
                <div>
                  <Common.Input
                    label="Postcode"
                    isAsterisk
                    type="text"
                    value={selectedLocation?.components?.postcode}
                    onChange={(e) =>
                      handleManualAddressChange("postcode", e.target.value)
                    }
                    placeholder="Postcode"
                    className="w-full"
                  />
                </div>

                <div>
                  <Common.Input
                    label="Country"
                    isAsterisk
                    type="text"
                    value={selectedLocation?.components?.country}
                    onChange={(e) =>
                      handleManualAddressChange("country", e.target.value)
                    }
                    placeholder="Country"
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <Common.Button
            color="transparent"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Common.Button>
          <Common.Button
            onClick={handleConfirm}
            disabled={
              (!selectedLocation && editMode === "map") ||
              loading ||
              isGeocoding ||
              !selectedLocation.components
            }
            isLoading={loading || isGeocoding}
          >
            Confirm
          </Common.Button>
        </div>
      </div>
    </ModalContent>
  );
};

export default LocateAddressDialog;
